import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trash2, Stethoscope, Camera, Loader2, X, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PlantResult } from '@/components/plants/PlantResult';
import { DiagnosticResult } from '@/components/plants/DiagnosticResult';
import { DiagnosticHistory } from '@/components/plants/DiagnosticHistory';
import { CareCalendar } from '@/components/plants/CareCalendar';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { useSubscription } from '@/hooks/useSubscription';
import { UpgradeDialog } from '@/components/subscription/UpgradeDialog';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface PlantIdentification {
  id: string;
  image_url: string;
  popular_name: string;
  scientific_name: string;
  planting_season: string;
  soil_type: string;
  sunlight_needs: string;
  water_needs: string;
  cultivation_tips: string;
  created_at: string;
}

interface DiagnosticData {
  health_score: number;
  problems: Array<{
    name: string;
    severity: 'low' | 'moderate' | 'high';
    description: string;
    solution: string;
  }>;
  recommendations: string;
}

export default function PlantDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated, handleAuthError } = useAuthGuard();
  const [plant, setPlant] = useState<PlantIdentification | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Diagnostic state
  const [showDiagnosticCapture, setShowDiagnosticCapture] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosticResult, setDiagnosticResult] = useState<DiagnosticData | null>(null);
  const [diagnosticImageUrl, setDiagnosticImageUrl] = useState<string | null>(null);
  const [refreshHistory, setRefreshHistory] = useState(0);
  const [isFirstDiagnostic, setIsFirstDiagnostic] = useState(true);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const { canDiagnose, monthlyUsage, logUsage, limits } = useSubscription();

  // Check if this is the first diagnostic
  useEffect(() => {
    const checkExistingDiagnostics = async () => {
      if (!id || !isAuthenticated()) return;
      
      const { count, error } = await supabase
        .from('plant_diagnostics')
        .select('id', { count: 'exact', head: true })
        .eq('plant_id', id)
        .eq('user_id', user?.id); // Explicit user_id filter
      
      if (error) {
        const wasAuthError = await handleAuthError(error);
        if (wasAuthError) return;
      }
      
      setIsFirstDiagnostic(count === 0);
    };
    
    checkExistingDiagnostics();
  }, [id, refreshHistory, user?.id, isAuthenticated, handleAuthError]);

  const fetchPlant = useCallback(async () => {
    // Verify authentication before fetching
    if (!isAuthenticated()) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('plant_identifications')
        .select('*')
        .eq('id', id)
        .eq('user_id', user?.id) // Explicit user_id filter as extra security layer
        .maybeSingle();

      if (error) {
        // Check if it's an auth error
        const wasAuthError = await handleAuthError(error);
        if (wasAuthError) return;
        
        console.error('Error fetching plant:', error);
        toast.error('Erro ao carregar planta');
        navigate('/collection');
        return;
      }

      if (!data) {
        toast.error('Planta não encontrada');
        navigate('/collection');
        return;
      }

      setPlant(data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao carregar planta');
      navigate('/collection');
    } finally {
      setLoading(false);
    }
  }, [id, user?.id, isAuthenticated, handleAuthError, navigate]);

  useEffect(() => {
    if (user && id) {
      fetchPlant();
    } else if (!user) {
      setLoading(false);
    }
  }, [user, id, fetchPlant]);

  const handleDelete = async () => {
    if (!plant) return;

    // Verify authentication before deleting
    if (!isAuthenticated()) {
      toast.error('Você precisa estar logado para remover plantas.');
      return;
    }

    try {
      const { error } = await supabase
        .from('plant_identifications')
        .delete()
        .eq('id', plant.id)
        .eq('user_id', user?.id); // Explicit user_id filter as extra security layer

      if (error) {
        // Check if it's an auth error
        const wasAuthError = await handleAuthError(error);
        if (wasAuthError) return;
        
        console.error('Error deleting plant:', error);
        toast.error('Erro ao remover planta');
        return;
      }

      toast.success('Planta removida da coleção');
      navigate('/collection');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao remover planta');
    }
  };

  const handleStartDiagnostic = () => {
    // Verify authentication before starting diagnostic
    if (!isAuthenticated()) {
      toast.error('Você precisa estar logado para fazer diagnósticos.');
      return;
    }

    if (!canDiagnose) {
      setShowUpgradeDialog(true);
      return;
    }
    setShowDiagnosticCapture(true);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !plant || !user) return;

    // Verify authentication before processing
    if (!isAuthenticated()) {
      toast.error('Você precisa estar logado para fazer diagnósticos.');
      return;
    }

    // Check limit before processing
    if (!canDiagnose) {
      setShowUpgradeDialog(true);
      return;
    }

    setDiagnosing(true);
    setShowDiagnosticCapture(false);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const dataUrl = e.target?.result as string;
        const base64 = dataUrl.split(',')[1];

        const fileName = `${user.id}/${plant.id}/diagnostic-${Date.now()}.jpg`;
        const { error: uploadError } = await supabase.storage
          .from('plant-images')
          .upload(fileName, file, { contentType: file.type });

        if (uploadError) {
          // Check if it's an auth error
          const wasAuthError = await handleAuthError(uploadError);
          if (wasAuthError) return;
          
          console.error('Upload error:', uploadError);
          toast.error('Erro ao fazer upload da imagem');
          setDiagnosing(false);
          return;
        }

        const { data: urlData } = supabase.storage
          .from('plant-images')
          .getPublicUrl(fileName);

        const imageUrl = urlData.publicUrl;
        setDiagnosticImageUrl(imageUrl);

        const { data, error } = await supabase.functions.invoke('diagnose-plant', {
          body: {
            imageBase64: base64,
            plantName: plant.popular_name,
            scientificName: plant.scientific_name
          }
        });

        if (error) {
          console.error('Diagnose error:', error);
          toast.error('Erro ao analisar a planta');
          setDiagnosing(false);
          return;
        }

        // Log usage after successful diagnostic
        await logUsage('diagnostic');

        const { error: saveError } = await supabase
          .from('plant_diagnostics')
          .insert({
            plant_id: plant.id,
            user_id: user.id,
            image_url: imageUrl,
            health_score: data.health_score,
            problems: data.problems,
            recommendations: data.recommendations
          });

        if (saveError) {
          // Check if it's an auth error
          const wasAuthError = await handleAuthError(saveError);
          if (wasAuthError) return;
          
          console.error('Save error:', saveError);
          toast.error('Erro ao salvar diagnóstico');
        }

        setDiagnosticResult(data);
        setRefreshHistory(prev => prev + 1);
        toast.success('Diagnóstico concluído!');
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao processar imagem');
    } finally {
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  const closeDiagnosticResult = () => {
    setDiagnosticResult(null);
    setDiagnosticImageUrl(null);
    setDiagnosing(false);
  };

  const handleUseIdentificationImage = async () => {
    if (!plant || !user) return;

    // Verify authentication before processing
    if (!isAuthenticated()) {
      toast.error('Você precisa estar logado para fazer diagnósticos.');
      return;
    }

    // Check limit before processing
    if (!canDiagnose) {
      setShowUpgradeDialog(true);
      return;
    }

    setDiagnosing(true);
    setShowDiagnosticCapture(false);

    try {
      const response = await fetch(plant.image_url);
      const blob = await response.blob();
      
      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = (reader.result as string).split(',')[1];
          resolve(result);
        };
        reader.readAsDataURL(blob);
      });

      setDiagnosticImageUrl(plant.image_url);

      const { data, error } = await supabase.functions.invoke('diagnose-plant', {
        body: {
          imageBase64: base64,
          plantName: plant.popular_name,
          scientificName: plant.scientific_name
        }
      });

      if (error) {
        console.error('Diagnose error:', error);
        toast.error('Erro ao analisar a planta');
        setDiagnosing(false);
        return;
      }

      // Log usage after successful diagnostic
      await logUsage('diagnostic');

      const { error: saveError } = await supabase
        .from('plant_diagnostics')
        .insert({
          plant_id: plant.id,
          user_id: user.id,
          image_url: plant.image_url,
          health_score: data.health_score,
          problems: data.problems,
          recommendations: data.recommendations
        });

      if (saveError) {
        // Check if it's an auth error
        const wasAuthError = await handleAuthError(saveError);
        if (wasAuthError) return;
        
        console.error('Save error:', saveError);
        toast.error('Erro ao salvar diagnóstico');
      }

      setDiagnosticResult(data);
      setRefreshHistory(prev => prev + 1);
      toast.success('Diagnóstico concluído!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao processar imagem');
      setDiagnosing(false);
    }
  };

  const DeleteButton = (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-10 w-10 text-destructive hover:text-destructive">
          <Trash2 className="h-5 w-5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remover planta?</AlertDialogTitle>
          <AlertDialogDescription>
            Esta ação não pode ser desfeita. A planta será removida permanentemente da sua coleção.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
            Remover
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return (
    <div className="min-h-screen bg-background pb-24">
      <MobileHeader 
        title={plant?.popular_name || 'Detalhes da Planta'} 
        showBack
        rightElement={!loading && plant ? DeleteButton : undefined}
      />

      <main className="px-4 py-4 space-y-6">
        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-64 w-full rounded-xl" />
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <div className="grid grid-cols-2 gap-3 mt-6">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-20 w-full rounded-xl" />
              ))}
            </div>
          </div>
        ) : plant ? (
          <>
            <PlantResult
              plant={{
                popular_name: plant.popular_name || 'Planta não identificada',
                scientific_name: plant.scientific_name || 'Nome científico desconhecido',
                planting_season: plant.planting_season || 'Não informado',
                soil_type: plant.soil_type || 'Não informado',
                sunlight_needs: plant.sunlight_needs || 'Não informado',
                water_needs: plant.water_needs || 'Não informado',
                cultivation_tips: plant.cultivation_tips || 'Sem dicas disponíveis',
              }}
              imageUrl={plant.image_url}
              onSave={() => {}}
              saved={true}
            />

            {/* Care Calendar */}
            <CareCalendar plantId={plant.id} />

            {/* Diagnostic Section */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Stethoscope className="h-5 w-5 text-primary" />
                Saúde da Planta
              </h2>

              {/* Hidden file inputs */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFileSelect}
              />
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileSelect}
              />

              {/* Diagnostic capture buttons */}
              {!diagnosing && !diagnosticResult && !showDiagnosticCapture && (
                <Button
                  onClick={handleStartDiagnostic}
                  className="w-full"
                  variant="outline"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Fazer Diagnóstico
                </Button>
              )}

              {showDiagnosticCapture && (
                <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                  <p className="text-sm text-muted-foreground text-center">
                    {isFirstDiagnostic 
                      ? 'Escolha uma opção para analisar a saúde da planta'
                      : 'Tire uma foto da planta para analisar sua saúde'
                    }
                  </p>
                  
                  {isFirstDiagnostic && (
                    <Button
                      onClick={handleUseIdentificationImage}
                      variant="secondary"
                      className="w-full"
                    >
                      <Image className="h-4 w-4 mr-2" />
                      Usar foto de identificação
                    </Button>
                  )}
                  
                  <div className="flex gap-3">
                    <Button
                      onClick={() => cameraInputRef.current?.click()}
                      className="flex-1"
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      Câmera
                    </Button>
                    <Button
                      onClick={() => galleryInputRef.current?.click()}
                      variant="outline"
                      className="flex-1"
                    >
                      Galeria
                    </Button>
                  </div>
                  <Button
                    onClick={() => setShowDiagnosticCapture(false)}
                    variant="ghost"
                    className="w-full"
                  >
                    Cancelar
                  </Button>
                </div>
              )}

              {/* Loading state */}
              {diagnosing && !diagnosticResult && (
                <div className="flex flex-col items-center justify-center py-8 space-y-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Analisando saúde da planta...</p>
                </div>
              )}

              {/* Diagnostic Result */}
              {diagnosticResult && diagnosticImageUrl && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Resultado do Diagnóstico</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={closeDiagnosticResult}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Fechar
                    </Button>
                  </div>
                  <DiagnosticResult
                    healthScore={diagnosticResult.health_score}
                    problems={diagnosticResult.problems}
                    recommendations={diagnosticResult.recommendations}
                    imageUrl={diagnosticImageUrl}
                  />
                </div>
              )}

              {/* Diagnostic History */}
              <DiagnosticHistory plantId={plant.id} refreshTrigger={refreshHistory} />
            </div>
          </>
        ) : null}
      </main>

      <BottomNavigation />

      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        currentUsage={monthlyUsage}
        maxUsage={limits.monthlyAiUsage}
        limitType="ai"
      />
    </div>
  );
}
